import { useFirestore, useFirestoreDocData } from 'reactfire'
import { doc } from 'firebase/firestore'
import { useActingUid } from './useActingUid'

// How loud the operator inbox is allowed to be.
//
// 'all' is the default because it is what the inbox has always done — nobody's
// experience changes until they choose otherwise.
export const CHATWOOT_SOUND_MODES = ['all', 'mine', 'off'] as const
export type ChatwootSoundMode = (typeof CHATWOOT_SOUND_MODES)[number]

export const DEFAULT_CHATWOOT_SOUND_MODE: ChatwootSoundMode = 'all'

export function asChatwootSoundMode(value: unknown): ChatwootSoundMode {
  return CHATWOOT_SOUND_MODES.includes(value as ChatwootSoundMode)
    ? (value as ChatwootSoundMode)
    : DEFAULT_CHATWOOT_SOUND_MODE
}

// Keyed on the ACTING operator, not the session user. useActingUid exists for
// exactly this ("their pinned teammates and companies, their sidebar order,
// their preferences"): while impersonating a colleague, the person
// hearing the chime is still the impersonator, so it is their preference that
// should apply.
//
// The settings form in MyAccount writes under the SESSION uid, which is the same
// value in any normal session. Under impersonation the two diverge on purpose:
// the inbox follows the operator's own ears, while the settings page edits the
// profile it is actually displaying. Deliberately not how `useAgentSoundEnabled`
// (Pulse) does it — that one predates useActingUid and still reads the session
// uid.
export function useChatwootSoundPreference(): ChatwootSoundMode {
  const firestore = useFirestore()
  const { actingUid } = useActingUid()
  // reactfire needs a concrete path even before the operator resolves; the
  // placeholder document does not exist, so the default applies until it does.
  const uid = actingUid ?? '_placeholder'

  const { data: profile } = useFirestoreDocData(
    doc(firestore, 'userprofiles', uid)
  )

  return asChatwootSoundMode(
    (profile as { notificationSettings?: { chatwoot?: { sound?: unknown } } })
      ?.notificationSettings?.chatwoot?.sound
  )
}
